import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ROUTES } from "../../app/utils/CONSTANTS";
import { handleMalwareDetection, isMalwareDetected } from "../../app/utils/security";
import { importFromExcelChunkedAPICall } from "./ImportFromExcelChunkedSlice";

export interface ImportFieldError {
  field: string;
  message: string;
  blockedChars: string[];
  errorType: string;
}

interface ImportErrorPayload {
  message: string;
  fieldErrors: ImportFieldError[];
}

const initialState = {
  data: null,
  status: "idle",
  error: null as string | null,
  fieldErrors: [] as ImportFieldError[],
};

export const importFromExcelAPICall = createAsyncThunk(
  "empOracleDetails/importFromExcelAPICall",
  async (
    {
      file,
    }: { file: File },
    { rejectWithValue }
  ) => {
    try {
      // Convert file to base64
      const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
        });
      };

      const base64Data = await fileToBase64(file);

      // Send as JSON instead of FormData
      const response = await fetch(ROUTES.importFromExcel, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileData: base64Data,
          fileName: file.name
        }),
        credentials: "include",
      });

      // Check for malware detection (backend returns 403 with malwareDetected flag)
      if (isMalwareDetected(response.status)) {
        try {
          const malwareData = await response.json();
          if (malwareData?.malwareDetected) {
            return await handleMalwareDetection();
          }
        } catch {
          // If JSON parse fails on 403, still treat as malware
          return await handleMalwareDetection();
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.errorSummary || errorData.message || "Failed to upload file";
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          return rejectWithValue({
            message: errorMessage,
            fieldErrors: errorData.errors.map((e: any) => ({
              field: e.field || '',
              message: e.message || '',
              blockedChars: e.blockedChars || [],
              errorType: e.errorType || ''
            }))
          });
        }
        return rejectWithValue({ message: errorMessage, fieldErrors: [] });
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue({ message: error.message || "Failed to import Excel file", fieldErrors: [] });
    }
  }
);

const importFromExcelAPICallSlice = createSlice({
  name: "empOracleDetails",
  initialState,
  reducers: {
    resetImportFromExcel: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = null;
      state.fieldErrors = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(importFromExcelAPICall.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.fieldErrors = [];
      })
      .addCase(importFromExcelAPICall.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload as any;
      })
      .addCase(importFromExcelAPICall.rejected, (state, action) => {
        state.status = "failed";
        const payload = action.payload as ImportErrorPayload | string;
        if (typeof payload === 'object' && payload !== null) {
          state.error = payload.message;
          state.fieldErrors = payload.fieldErrors || [];
        } else {
          state.error = payload as string;
          state.fieldErrors = [];
        }
      })
      // Chunked Upload Handlers
      .addCase(importFromExcelChunkedAPICall.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.fieldErrors = [];
      })
      .addCase(importFromExcelChunkedAPICall.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload as any;
      })
      .addCase(importFromExcelChunkedAPICall.rejected, (state, action) => {
        state.status = "failed";
        const payload = action.payload as ImportErrorPayload | string;
        if (typeof payload === 'object' && payload !== null) {
          state.error = payload.message;
          state.fieldErrors = payload.fieldErrors || [];
        } else {
          state.error = payload as string;
          state.fieldErrors = [];
        }
      });
  },
});

export const { resetImportFromExcel } = importFromExcelAPICallSlice.actions;

export default importFromExcelAPICallSlice.reducer;
